import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Select, Alert, Breadcrumbs, PanelCard, Icon } from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import { vendorApiClient, VendorApiError, type CreateLocationInput, type VendorLocation } from '../../lib/vendorApiClient';
import { defaultWeek, normalizeWeek, hoursTodayLabel, type WeekHours } from '../../lib/hours';
import { toE164 } from '../../lib/phone';
import { LocationDetailsCard, HoursCard, ServicesCard, SnapCard, ShopperPreviewCard, type LocationFormState } from './LocationForm';

/**
 * L3 — Add a location (Figma 11:129). Submitting creates a Store row in
 * `verifying`; vendor-api geocodes the address (street-level, relevance ≥ 0.8)
 * and refuses anything it cannot place. A human confirms the location before
 * shoppers can see it (§15gg V8) — the "what happens next" card says so.
 */

const HEADING = '#303A42';
const MUTED = '#5A7063';

function emptyForm(): LocationFormState {
  return {
    name: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    phone: '',
    website: '',
    hours: defaultWeek(),
    pickupSupported: true,
    temporarilyClosed: false,
    fnsNumber: '',
  };
}

export default function LocationAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeVendorId, activeMembership, has } = useVendor();
  const canManage = has('location:manage');
  const [form, setForm] = useState<LocationFormState>(emptyForm);
  const [existing, setExisting] = useState<VendorLocation[]>([]);
  const [copyFrom, setCopyFrom] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeVendorId) return;
    vendorApiClient
      .listLocations(activeVendorId)
      .then((r) => setExisting(r.items))
      .catch(() => setExisting([]));
  }, [activeVendorId]);

  const patch = (p: Partial<LocationFormState>) => setForm((f) => ({ ...f, ...p }));

  const onCopyFrom = (storeId: string) => {
    setCopyFrom(storeId);
    const src = existing.find((l) => l.storeId === storeId);
    if (!src) return;
    patch({ hours: normalizeWeek(src.hours), pickupSupported: src.pickupSupported });
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return t('locations.errors.nameRequired');
    if (!form.line1.trim() || !form.city.trim() || !form.state.trim() || !form.postalCode.trim()) return t('locations.errors.addressRequired');
    if (form.phone.trim() && !toE164(form.phone)) return t('locations.errors.phoneInvalid');
    if (form.fnsNumber && form.fnsNumber.length !== 7) return t('locations.errors.fnsInvalid');
    return null;
  };

  const submit = async () => {
    if (!activeVendorId) return;
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const input: CreateLocationInput = {
        name: form.name.trim(),
        address: {
          line1: form.line1.trim(),
          line2: form.line2.trim() || undefined,
          city: form.city.trim(),
          state: form.state.trim().toUpperCase(),
          postalCode: form.postalCode.trim(),
        },
        phoneE164: toE164(form.phone) ?? undefined,
        website: form.website.trim() || undefined,
        hours: weekToApi(form.hours),
        pickupSupported: form.pickupSupported,
        fnsNumber: form.fnsNumber || undefined,
      };
      const created = await vendorApiClient.createLocation(activeVendorId, input);
      navigate(`/locations/${created.storeId}`, { replace: true });
    } catch (e) {
      setError(describeError(e, t));
    } finally {
      setSubmitting(false);
    }
  };

  if (!canManage) {
    return <Alert severity="warning">{t('locations.errors.noManage')}</Alert>;
  }

  const today = hoursTodayLabel(form.hours);

  return (
    <Box>
      <Breadcrumbs
        ariaLabel={t('locations.breadcrumbAria')}
        linkComponent={Link}
        items={[
          { label: t('nav.locations'), to: '/locations' },
          { label: t('locations.addLocation'), ariaCurrent: true },
        ]}
      />
      <Typography component="h1" sx={{ mt: 1, fontSize: 40, lineHeight: 1.2, fontWeight: 700, color: HEADING }}>
        {t('locations.addLocation')}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 16, color: MUTED }}>{t('locations.add.subtitle')}</Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' }, gap: 3, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <LocationDetailsCard form={form} onChange={patch} disabled={submitting} />

          {existing.length > 0 && (
            <Box sx={{ maxWidth: 420 }}>
              <Select
                label={t('locations.add.copyFrom')}
                ariaLabel={t('locations.add.copyFrom')}
                placeholder={t('locations.add.copyFromPlaceholder')}
                value={copyFrom}
                onChange={(_e, v) => onCopyFrom(v)}
                options={existing.map((l) => ({ value: l.storeId, label: l.name }))}
                size="small"
                fullWidth
              />
            </Box>
          )}

          <HoursCard hours={form.hours} onChange={(hours) => patch({ hours })} disabled={submitting} />
          <ServicesCard form={form} onChange={patch} disabled={submitting} showTemporarilyClosed={false} />
          <SnapCard form={form} onChange={patch} verification={null} disabled={submitting} />

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button ariaLabel={t('common.cancel')} buttonText={t('common.cancel')} variant="outlined" onClick={() => navigate('/locations')} disabled={submitting} />
            <Button
              ariaLabel={t('locations.add.submit')}
              buttonText={submitting ? t('locations.add.submitting') : t('locations.add.submit')}
              variant="contained"
              color="primary"
              onClick={() => void submit()}
              disabled={submitting}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <PanelCard title={t('locations.add.nextTitle')} accent="#F67A1A" sx={{ height: 'auto', bgcolor: '#fff' }} headingLevel="h3">
            <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {(['verify', 'products', 'live'] as const).map((k) => (
                <Typography key={k} component="li" sx={{ fontSize: 14, color: '#343434', lineHeight: 1.5 }}>
                  {t(`locations.add.next.${k}`)}
                </Typography>
              ))}
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start', color: MUTED }}>
              <Icon name="info" size={18} color="#5A7063" />
              <Typography sx={{ fontSize: 12, color: MUTED }}>{t('locations.add.nextNote')}</Typography>
            </Box>
          </PanelCard>
          <ShopperPreviewCard
            name={form.name}
            businessType={activeMembership?.businessName ?? ''}
            hoursText={today.open ? `${t('locations.openToday')} · ${today.text}` : t('locations.closedToday')}
            snap={false}
            pickup={form.pickupSupported}
            live={false}
          />
        </Box>
      </Box>
    </Box>
  );
}

export function weekToApi(week: WeekHours): CreateLocationInput['hours'] {
  const out: NonNullable<CreateLocationInput['hours']> = {};
  for (const [day, d] of Object.entries(week)) {
    out[day] = d.closed || !d.open || !d.close ? { closed: true } : { open: d.open, close: d.close, closed: false };
  }
  return out;
}

/**
 * vendor-api already writes its refusals for people ("We couldn't find that
 * street address…", "Address lookup is unavailable right now…"), so they are
 * shown verbatim; anything else falls back to a generic line.
 */
const TRANSLATED_ERROR_CODES = ['location_exists', 'location_claimed'];

export function describeError(e: unknown, t: (k: string) => string): string {
  if (e instanceof VendorApiError && e.code && TRANSLATED_ERROR_CODES.includes(e.code)) {
    return t(`locations.errors.${e.code}`);
  }
  return e instanceof Error && e.message ? e.message : t('locations.errors.generic');
}
